/**
 * House Prices Service
 * Handles all API calls related to property prices and data
 */

import { apiRequest } from './api';
import config from '../config';

/**
 * Fetch house prices data for a postcode
 *
 * @param {string} postcode - The postcode to get prices for
 * @returns {Promise<Array>} - Array of house price records
 */
const getHousePrices = async (postcode) => {
  if (!postcode) {
    console.error('No postcode provided to getHousePrices');
    return [];
  }

  const url = `${config.apiUrl}/house-prices?postcode=${encodeURIComponent(postcode)}`;

  // Create a cache key based on the postcode
  const cacheKey = `house_prices_${postcode}`;

  // House prices data doesn't change frequently, so we can cache it for longer
  const cacheDuration = 24 * 60 * 60 * 1000; // 24 hours

  try {
    const response = await apiRequest(url, {}, cacheKey, cacheDuration);

    // Handle new API response format with 'sales' and 'regionPriceIndex' properties
    const salesData = response && response.sales ? response.sales : [];
    const regionPriceIndex =
      response && response.regionPriceIndex ? response.regionPriceIndex : null;

    // Store regionPriceIndex in sessionStorage for potential use elsewhere
    if (regionPriceIndex) {
      sessionStorage.setItem('regionPriceIndex_' + postcode, JSON.stringify(regionPriceIndex));
    }

    // Validate the sales data
    if (!Array.isArray(salesData)) {
      console.error('Invalid house prices data format:', salesData);
      return [];
    }

    // Transform and sort the data
    const transformedSales = salesData.map((sale) => {
      // Ensure sale is an object
      if (!sale || typeof sale !== 'object') {
        console.error('Invalid sale data:', sale);
        return null;
      }

      // Ensure address is an object
      const address = sale.address && typeof sale.address === 'object' ? sale.address : {};

      return {
        address: {
          county: address.county || '',
          paon: address.paon || '',
          saon: address.saon || '',
          street: address.street || '',
          town: address.town || '',
        },
        category: sale.category || 'Standard price paid transaction',
        date: sale.date || new Date().toISOString(),
        estateType: sale.estateType || 'Freehold',
        newBuild: Boolean(sale.newBuild),
        postcode: sale.postcode || postcode,
        price: typeof sale.price === 'number' ? sale.price : 0,
        propertyType: sale.propertyType || 'terraced',
      };
    });

    // Filter out null values
    const validSales = transformedSales.filter((sale) => sale !== null);

    // Sort sales by date in descending order
    const sortedSales = validSales.sort((a, b) => new Date(b.date) - new Date(a.date));

    // Return the complete response structure with both sales and regionPriceIndex
    return {
      sales: sortedSales,
      regionPriceIndex: regionPriceIndex,
    };
  } catch (error) {
    console.error('Error fetching house prices:', error);
    return [];
  }
};

/**
 * Calculate the annual growth rate based on house price data
 *
 * @param {Array} priceData - Array of house price records
 * @returns {number} - Annual growth rate as a percentage
 */
const calculateAnnualGrowth = (priceData) => {
  if (priceData.length < 2) return 0;

  const latestPrice = priceData[0].price;
  const oldestPrice = priceData[priceData.length - 1].price;
  const yearDiff =
    (new Date(priceData[0].date) - new Date(priceData[priceData.length - 1].date)) /
    (1000 * 60 * 60 * 24 * 365);

  if (yearDiff <= 0) return 0;

  return (Math.pow(latestPrice / oldestPrice, 1 / yearDiff) - 1) * 100;
};

/**
 * Get average rents for the area (placeholder for future functionality)
 *
 * @param {string} postcode - The postcode to get rents for
 * @returns {Promise<Object>} - Average rent data
 */
const getAverageRents = async (postcode) => {
  // This is a placeholder for future functionality
  // Currently we're returning mock data, but this should be replaced with an API call

  return {
    '2 bed': '£1,200',
    '3 bed': '£1,500',
  };
};

/**
 * Get all property price data for a location
 *
 * @param {Object} addressDetails - Address details including postcode
 * @returns {Promise<Object>} - Combined property price data
 */
const getAllPropertyData = async (addressDetails) => {
  try {
    const housePrices = await getHousePrices(addressDetails.postcode);
    const annualGrowth = calculateAnnualGrowth(housePrices);
    const averageRents = await getAverageRents(addressDetails.postcode);

    return {
      sales: housePrices,
      annualGrowth,
      averageRents,
      isLoading: false,
    };
  } catch (error) {
    console.error('Error fetching property data:', error);
    throw error;
  }
};

/**
 * Fetch postcode data from postcodes.io API
 *
 * @param {string} postcode - The postcode to get data for
 * @returns {Promise<Object>} - Postcode data including lat, lng, and region
 */
const getPostcodeData = async (postcode) => {
  if (!postcode) {
    console.error('No postcode provided to getPostcodeData');
    return null;
  }

  try {
    const response = await fetch(
      `https://api.postcodes.io/postcodes/${encodeURIComponent(postcode.trim())}`,
    );

    if (response.ok) {
      const data = await response.json();
      if (data && data.result) {
        const { latitude, longitude, region } = data.result;
        return {
          latitude,
          longitude,
          region,
        };
      }
    }

    return null;
  } catch (error) {
    console.error('Error fetching postcode data:', error);
    return null;
  }
};

export {
  getHousePrices,
  calculateAnnualGrowth,
  getAverageRents,
  getAllPropertyData,
  getPostcodeData,
};
